import * as React from "react";
import {FormControl, InputLabel, MenuItem, Select} from "@mui/material";

const HOUR_OPTIONS = Array.from({length: 24}, (_, h) => h);

export type HourValue = number | "";

export function hourLabel(h: number): string {
    if (h === 0) {
        return "12am";
    }
    if (h < 12) {
        return `${h}am`;
    }
    if (h === 12) {
        return "12pm";
    }
    return `${h - 12}pm`;
}

export function hourFilterActive(startHour: HourValue, endHour: HourValue): boolean {
    return startHour !== "" && endHour !== "" && startHour <= endHour;
}

/**
 * Dashboard filter-bar hour-of-day range (Pacific). Empty = no hour constraint.
 * Sized to match DateRange / publisher MultiSelect in the Edu dashboard bar.
 */
export default function HourRange({
    startHour,
    endHour,
    setStartHour,
    setEndHour,
    style,
}: {
    startHour: HourValue;
    endHour: HourValue;
    setStartHour: (v: HourValue) => void;
    setEndHour: (v: HourValue) => void;
    style?: React.CSSProperties;
}) {
    const controlSx = {
        marginLeft: "5px",
        width: window.innerWidth < 768 ? "78px" : "88px",
        "& .MuiInputLabel-root": {fontSize: "13px"},
        "& .MuiSelect-select": {fontSize: "13px", py: "6.5px"},
        "& .MuiOutlinedInput-notchedOutline": {borderColor: "#ced4da"},
        "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: "#ced4da"},
    };

    return (
        <div style={{display: "flex", alignItems: "center", ...(style || {})}}>
            <FormControl size="small" variant="outlined" sx={controlSx}>
                <InputLabel id="hour-range-from">From</InputLabel>
                <Select
                    labelId="hour-range-from"
                    label="From"
                    value={startHour}
                    onChange={(e) => {
                        const v = e.target.value === "" ? "" : Number(e.target.value);
                        setStartHour(v);
                        if (v !== "" && endHour !== "" && v > endHour) {
                            setEndHour(v);
                        }
                    }}
                >
                    <MenuItem value="">Any</MenuItem>
                    {HOUR_OPTIONS.map((h) => (
                        <MenuItem key={h} value={h}>{hourLabel(h)}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" variant="outlined" sx={controlSx}>
                <InputLabel id="hour-range-to">To</InputLabel>
                <Select
                    labelId="hour-range-to"
                    label="To"
                    value={endHour}
                    onChange={(e) => {
                        const v = e.target.value === "" ? "" : Number(e.target.value);
                        setEndHour(v);
                        if (v !== "" && startHour !== "" && v < startHour) {
                            setStartHour(v);
                        }
                    }}
                >
                    <MenuItem value="">Any</MenuItem>
                    {HOUR_OPTIONS.map((h) => (
                        <MenuItem key={h} value={h}>{hourLabel(h)}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );
}
